import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import React, { useState } from "react";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import useModelComputeOptions from "../../hooks/useModelComputeOptions";
import { ModelComputeFrequency, ModelOption } from "models/ModelCompute";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import OrganizationService from "../../Organization.service";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { AuthStore } from "stores/AuthStore";
import AcxSwitch from "components/UI/AcxSwitch";
import useTranscriptionFilterOptions from "../../hooks/useTranscriptionFilterOptions";
import { InteractionType } from "models/InteractionType";
import useRedactionOptions, {
    extractPIIString,
    persistTranscriptString,
} from "../../hooks/useRedactionOptions";
import usePIIOptions from "../../hooks/usePIIOptions";
import { ModelComputeService } from "services/ModelComputeService";
import SafetyEventModelControls from "./SafetyEventModelControls";
import RedactionModelControls from "./RedactionModelControls";
import HotWordsControls from "./HotWordsControls";
import useLanguageDetection from "../../hooks/useLanguageDetection";
import LanguageDetectionControls from "./LanguageDetectionControls";
import PatientJourneyStagesDialog from "components/UI/PatientJourneyStagesDialog";
import { styled } from "@mui/material/styles";
import IVRControls from "./IVRControls";
import VoicemailControls from "./VoicemailControls";
import useModelHierarchies from "../../hooks/useModelHierarchies";
import useActiveHotWords from "../../hooks/useActiveHotWords";
import { OrganizationService as SharedOrganizationService } from "services/OrganizationService";
import ServiceHierarchySelect from "../OrganizationModules/fields/ServiceHierarchySelect";
import QosService, {
    QosConfiguration,
    QosServiceLevel,
} from "services/QosService";
import { v4 as uuidv4 } from "uuid";
import * as AYSDialog from "components/UI/AYSDialog";

type Props = {
    id?: string;
};

const PV_MODEL_OPTIONS = [
    "AdverseEvents",
    "AdverseEventsP2",
    "PQC",
    "SpecialSituations",
];

type ModelControlChangeSummaryItem = {
    property: string;
    previousValue?: string;
    newValue?: string;
    addedValues?: string;
    removedValues?: string;
};

type ModelControlSnapshotModelOption = {
    modelOption: string;
    enabled?: boolean;
    runOnAllHierarchies?: boolean;
    organizationStructureMemberIds?: string[];
    quotaFrequency?: number;
    modelQuota?: number;
};

type ModelControlSnapshotTranscriptionFilter = {
    filterType: string;
    enabled?: boolean;
    mediaType: string;
    filterValue?: number;
};

type ModelControlSnapshot = {
    modelOptions?: ModelControlSnapshotModelOption[];
    transcriptionFilterOptions?: ModelControlSnapshotTranscriptionFilter[];
    redactionProcessingOptions?: string[];
    piiExtractionOptions?: string[];
    voiceRedactionFactor?: number;
    hotWords?: {
        transcriptionWords?: string[];
        redactionWords?: string[];
    };
    languageDetection?: {
        activeProcessingOptions?: string[];
        hierarchyIds?: string[];
        runOnAllHierarchies?: boolean;
    };
    qos?: {
        enabled?: boolean;
        fastLaneHierarchyIds?: string[];
        bulkLaneHierarchyIds?: string[];
    };
};

const formatSummaryValue = (value: unknown) => {
    if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "None";
    }

    if (typeof value === "boolean") {
        return value ? "Enabled" : "Disabled";
    }

    if (value === undefined || value === null || value === "") {
        return "None";
    }

    return String(value);
};

const valuesAreEqual = (previousValue: unknown, newValue: unknown) =>
    JSON.stringify(previousValue) === JSON.stringify(newValue);

const getChangeKey = (change: ModelControlChangeSummaryItem) =>
    `${change.property}-${change.previousValue}-${change.newValue}-${change.addedValues}-${change.removedValues}`;

const getAddedValues = (previousValues: string[], newValues: string[]) =>
    newValues.filter((value) => !previousValues.includes(value));

const getRemovedValues = (previousValues: string[], newValues: string[]) =>
    previousValues.filter((value) => !newValues.includes(value));

const formatOptionValues = (
    values: string[],
    useHierarchyLabels: boolean,
    hierarchyLabelById: Record<string, string>,
) => {
    const formattedValues = values.map((value) =>
        useHierarchyLabels ? hierarchyLabelById[value] ?? value : value,
    );

    return formattedValues.length ? formattedValues.join(", ") : "None";
};

const getModelControlChangeSummary = (
    savedSnapshot?: string,
    currentSnapshot?: string,
    hierarchyLabelById: Record<string, string> = {},
) => {
    if (!savedSnapshot || !currentSnapshot) {
        return [
            {
                property: "Model controls",
                previousValue: "Unknown",
                newValue: "Changed",
            },
        ];
    }

    try {
        const saved = JSON.parse(savedSnapshot) as ModelControlSnapshot;
        const current = JSON.parse(currentSnapshot) as ModelControlSnapshot;
        const changes: ModelControlChangeSummaryItem[] = [];

        const addChange = (
            property: string,
            previousValue: unknown,
            newValue: unknown,
        ) => {
            if (valuesAreEqual(previousValue, newValue)) {
                return;
            }

            const formattedPreviousValue = formatSummaryValue(previousValue);
            const formattedNewValue = formatSummaryValue(newValue);

            if (formattedPreviousValue === formattedNewValue) {
                return;
            }

            if (Array.isArray(previousValue) || Array.isArray(newValue)) {
                const previousValues = Array.isArray(previousValue)
                    ? previousValue.map(String)
                    : [];
                const newValues = Array.isArray(newValue)
                    ? newValue.map(String)
                    : [];
                const useHierarchyLabels = property
                    .toLowerCase()
                    .includes("hierarch");

                changes.push({
                    property,
                    addedValues: formatOptionValues(
                        getAddedValues(previousValues, newValues),
                        useHierarchyLabels,
                        hierarchyLabelById,
                    ),
                    removedValues: formatOptionValues(
                        getRemovedValues(previousValues, newValues),
                        useHierarchyLabels,
                        hierarchyLabelById,
                    ),
                });
                return;
            }

            changes.push({
                property,
                previousValue: formattedPreviousValue,
                newValue: formattedNewValue,
            });
        };

        const savedModelOptions = new Map(
            saved.modelOptions?.map((option) => [option.modelOption, option]) ??
                [],
        );
        const currentModelOptions = new Map(
            current.modelOptions?.map((option) => [
                option.modelOption,
                option,
            ]) ?? [],
        );
        const modelOptionNames = Array.from(
            new Set([
                ...savedModelOptions.keys(),
                ...currentModelOptions.keys(),
            ]),
        );

        modelOptionNames.forEach((modelOptionName) => {
            const savedOption = savedModelOptions.get(modelOptionName);
            const currentOption = currentModelOptions.get(modelOptionName);

            addChange(
                `${modelOptionName} enabled`,
                savedOption?.enabled,
                currentOption?.enabled,
            );
            addChange(
                `${modelOptionName} run on all hierarchies`,
                savedOption?.runOnAllHierarchies,
                currentOption?.runOnAllHierarchies,
            );
            addChange(
                `${modelOptionName} hierarchies`,
                savedOption?.organizationStructureMemberIds,
                currentOption?.organizationStructureMemberIds,
            );
            addChange(
                `${modelOptionName} quota frequency`,
                savedOption?.quotaFrequency,
                currentOption?.quotaFrequency,
            );
            addChange(
                `${modelOptionName} model quota`,
                savedOption?.modelQuota,
                currentOption?.modelQuota,
            );
        });

        const savedTranscriptionFilters = new Map(
            saved.transcriptionFilterOptions?.map((option) => [
                `${option.filterType}-${option.mediaType}`,
                option,
            ]) ?? [],
        );
        const currentTranscriptionFilters = new Map(
            current.transcriptionFilterOptions?.map((option) => [
                `${option.filterType}-${option.mediaType}`,
                option,
            ]) ?? [],
        );
        const transcriptionFilterNames = Array.from(
            new Set([
                ...savedTranscriptionFilters.keys(),
                ...currentTranscriptionFilters.keys(),
            ]),
        );

        transcriptionFilterNames.forEach((filterName) => {
            const savedFilter = savedTranscriptionFilters.get(filterName);
            const currentFilter = currentTranscriptionFilters.get(filterName);

            addChange(
                `${filterName} enabled`,
                savedFilter?.enabled,
                currentFilter?.enabled,
            );
            addChange(
                `${filterName} value`,
                savedFilter?.filterValue,
                currentFilter?.filterValue,
            );
        });

        addChange(
            "Redaction processing options",
            saved.redactionProcessingOptions,
            current.redactionProcessingOptions,
        );
        addChange(
            "PII extraction options",
            saved.piiExtractionOptions,
            current.piiExtractionOptions,
        );
        addChange(
            "Voice redaction factor",
            saved.voiceRedactionFactor,
            current.voiceRedactionFactor,
        );
        addChange(
            "Transcription hot words",
            saved.hotWords?.transcriptionWords,
            current.hotWords?.transcriptionWords,
        );
        addChange(
            "Redaction hot words",
            saved.hotWords?.redactionWords,
            current.hotWords?.redactionWords,
        );
        addChange(
            "Language detection processing options",
            saved.languageDetection?.activeProcessingOptions,
            current.languageDetection?.activeProcessingOptions,
        );
        addChange(
            "Language detection run on all hierarchies",
            saved.languageDetection?.runOnAllHierarchies,
            current.languageDetection?.runOnAllHierarchies,
        );
        addChange(
            "Language detection hierarchies",
            saved.languageDetection?.hierarchyIds,
            current.languageDetection?.hierarchyIds,
        );
        addChange(
            "Quality of service enabled",
            saved.qos?.enabled,
            current.qos?.enabled,
        );
        addChange(
            "Quality of service fast lane hierarchies",
            saved.qos?.fastLaneHierarchyIds,
            current.qos?.fastLaneHierarchyIds,
        );
        addChange(
            "Quality of service bulk lane hierarchies",
            saved.qos?.bulkLaneHierarchyIds,
            current.qos?.bulkLaneHierarchyIds,
        );

        return changes.length
            ? changes
            : [
                  {
                      property: "Model controls",
                      previousValue: "Unknown",
                      newValue: "Changed",
                  },
              ];
    } catch {
        return [
            {
                property: "Model controls",
                previousValue: "Unknown",
                newValue: "Changed",
            },
        ];
    }
};

// Styled components for model header and description
const ModelHeaderText = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    fontWeight: 600,
    fontSize: "14px",
}));
const ModelDescText = styled(Grid)(({ theme }) => ({
    fontWeight: 400,
    fontSize: "14px",
    color: theme.palette.gray.main,
    padding: theme.spacing(2),
}));

const OrganizationModelControls: React.FC<Props> = observer(({ id }) => {
    const messageStore = useStore(MessageStore);
    const authStore = useStore(AuthStore);
    const orgStore = authStore.orgStore;
    const selectedOrganizationId = orgStore.selectedOrganization?.id;
    const [savingModelCompute, setSavingModelCompute] = useState(false);
    const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
    const [hierarchyLabelById, setHierarchyLabelById] = useState<
        Record<string, string>
    >({});

    React.useEffect(() => {
        if (
            id &&
            id === selectedOrganizationId &&
            !orgStore.hasLoadedRedaction
        ) {
            void orgStore.loadRedactionEnabled(id);
        }
    }, [id, orgStore, orgStore.hasLoadedRedaction, selectedOrganizationId]);

    const {
        modelComputeOptions,
        isLoading: modelOptionsIsLoading,
        setModelComputeOptions,
    } = useModelComputeOptions({ id: id! });

    const modelComputeService = React.useMemo(() => {
        return new ModelComputeService();
    }, []);

    const {
        txHotWords,
        setTxHotWords,
        txHotWordsIsEnabled: hotWordsIsEnabled,
        setTxHotWordsIsEnabled: setHotWordsIsEnabled,
        redactHotWords,
        setRedactHotWords,
        redactHotWordsIsEnabled,
        setRedactHotWordsIsEnabled,
        isLoading: hotWordsIsLoading,
    } = useActiveHotWords(modelComputeService);

    const {
        isLoading: languageDetectionIsLoading,
        runOnAll: languageDetectionRunOnAll,
        setRunOnAll: setLanguageDetectionRunOnAll,
        isEnabled: languageDetectionIsEnabled,
        setIsEnabled: setLanguageDetectionIsEnabled,
        selectedHierarchiesIds: selectedLanguageDetectionHierarchiesIds,
        setSelectedHierarchiesIds: setSelectedLanguageDetectionHierarchiesIds,
        processingId: languageDetectionProcessingId,
    } = useLanguageDetection(modelComputeService);

    const {
        setSelectedHierarchies: setSelectedIVRHierarchies,
        selectedHierarchies: selectedIVRHierarchies,
        setRunOnAll: setIVRRunOnAll,
        runOnAll: ivrRunOnAll,
        isEnabled: ivrIsEnabled,
    } = useModelHierarchies(modelComputeOptions, "IVR");

    const {
        setSelectedHierarchies: setSelectedVoicemailHierarchies,
        selectedHierarchies: selectedVoicemailHierarchies,
        setRunOnAll: setVoicemailRunOnAll,
        runOnAll: voicemailRunOnAll,
        isEnabled: voicemailIsEnabled,
    } = useModelHierarchies(modelComputeOptions, "Voicemail");

    const { transcriptionFilterOptions, setTranscriptionFilterOptions } =
        useTranscriptionFilterOptions({
            id: id!,
        });

    const {
        redactionOptions,
        activeRedactionOptions,
        setActiveRedactionOptions,
        extractPIIEnabled,
        setExtractPIIEnabled,
        persistOriginalTranscript,
        setPersistOriginalTranscript,
        isLoading: redactionOptionsIsLoading,
        multiSelectRedactionOptions,
        voiceRedactionFactor,
        setVoiceRedactionFactor,
    } = useRedactionOptions({
        id: id!,
    });

    const qosService = React.useMemo(() => {
        return new QosService();
    }, []);
    const [qosIsLoading, setQosIsLoading] = useState(false);
    const [qosConfigurations, setQosConfigurations] = useState<
        QosConfiguration[]
    >([]);
    const [qosEnabled, setQosEnabled] = useState(false);
    const [qosFastLaneHierarchyIds, setQosFastLaneHierarchyIds] = useState<
        string[]
    >([]);
    const [qosBulkLaneHierarchyIds, setQosBulkLaneHierarchyIds] = useState<
        string[]
    >([]);
    const setQosState = React.useCallback(
        (qosConfigurationsResponse: QosConfiguration[]) => {
            setQosConfigurations(qosConfigurationsResponse);
            var activeQosConfigurations = qosConfigurationsResponse.filter(
                (x) => x.isActive,
            );
            setQosEnabled(activeQosConfigurations.length > 0);
            setQosFastLaneHierarchyIds(
                activeQosConfigurations
                    .filter(
                        (configuration) =>
                            configuration.serviceLevel ===
                            QosServiceLevel.Highest,
                    )
                    .map(
                        (configuration) =>
                            configuration.organizationStructureMemberId,
                    ),
            );
            setQosBulkLaneHierarchyIds(
                activeQosConfigurations
                    .filter(
                        (configuration) =>
                            configuration.serviceLevel === QosServiceLevel.Bulk,
                    )
                    .map(
                        (configuration) =>
                            configuration.organizationStructureMemberId,
                    ),
            );
        },
        [],
    );

    const isViewOnly =
        authStore.canUserView("Model Controls") &&
        !authStore.canUserEdit("Model Controls");

    const { PIIOptions, activePIIOptions, setActivePIIOptions } =
        usePIIOptions();
    const extractPIIDbStateRef = React.useRef<{
        initializedForOrgId?: string;
        hierarchyIds: string[];
        activePIIOptions: string[];
    }>({
        hierarchyIds: [],
        activePIIOptions: [],
    });

    const [savedModelControlsSnapshot, setSavedModelControlsSnapshot] =
        useState<string>();

    const SEOption = modelComputeOptions?.find(
        (i) => i.modelComputeOption.modelOption === "SafetyEvents",
    );
    const SEEnabled = !!SEOption?.enabled;

    const SEDefaultFreq = SEOption?.modelComputeOption.quotaFrequency;
    const SEDefaultQuota = SEOption?.modelComputeOption.modelQuota;
    const SEDefaultOSMIds =
        SEOption?.modelComputeOption.modelComputeStructureMembers ?? [];

    const [isAll, setIsAll] = useState<boolean>(
        !(SEDefaultFreq || SEDefaultQuota || !!SEDefaultOSMIds.length),
    );
    const safetyEventsRunOnAllInitializedForOrgId = React.useRef<string>();

    React.useEffect(() => {
        orgStore.setModelComputeOptions(modelComputeOptions);
    }, [modelComputeOptions, orgStore]);

    const redactionOption = modelComputeOptions?.find(
        (i) => i.modelComputeOption.modelOption === "Redaction",
    );
    const redactionHierarchyIds = React.useMemo(
        () =>
            redactionOption?.modelComputeOption.modelComputeStructureMembers?.map(
                (i) => i.organizationStructureMemberId,
            ) ?? [],
        [redactionOption?.modelComputeOption.modelComputeStructureMembers],
    );

    const orgService = React.useMemo(() => {
        return OrganizationService;
    }, []);

    React.useEffect(() => {
        let destroyed = false;

        const loadQosConfigurations = async () => {
            if (!id) return;
            setQosIsLoading(true);

            try {
                const qosConfigurationsResponse =
                    await qosService.getQosConfigurations();

                if (destroyed) return;
                setQosState(qosConfigurationsResponse);
            } catch {
                if (destroyed) return;

                setQosConfigurations([]);
                setQosEnabled(false);
                setQosFastLaneHierarchyIds([]);
                setQosBulkLaneHierarchyIds([]);
            } finally {
                if (!destroyed) {
                    setQosIsLoading(false);
                }
            }
        };

        loadQosConfigurations();

        return () => {
            destroyed = true;
        };
    }, [setQosState, id, qosService]);

    React.useEffect(() => {
        let destroyed = false;

        const loadHierarchyLabels = async () => {
            if (!id) return;

            const hierarchyOptions =
                await new SharedOrganizationService().getFlattenedServiceHierarchy(
                    id,
                );

            if (destroyed) return;

            setHierarchyLabelById(
                Object.fromEntries(
                    hierarchyOptions.map((option) => [option.id, option.label]),
                ),
            );
        };

        loadHierarchyLabels();

        return () => {
            destroyed = true;
        };
    }, [id]);

    React.useEffect(() => {
        if (
            !id ||
            !modelComputeOptions ||
            safetyEventsRunOnAllInitializedForOrgId.current === id
        ) {
            return;
        }

        setIsAll(
            !(SEDefaultFreq || SEDefaultQuota || !!SEDefaultOSMIds.length),
        );
        safetyEventsRunOnAllInitializedForOrgId.current = id;
    }, [
        id,
        modelComputeOptions,
        SEDefaultFreq,
        SEDefaultOSMIds.length,
        SEDefaultQuota,
    ]);

    React.useEffect(() => {
        if (
            !id ||
            !modelComputeOptions ||
            activePIIOptions === undefined ||
            extractPIIDbStateRef.current.initializedForOrgId === id
        ) {
            return;
        }

        extractPIIDbStateRef.current = {
            initializedForOrgId: id,
            hierarchyIds: redactionHierarchyIds,
            activePIIOptions: activePIIOptions ?? [],
        };
    }, [activePIIOptions, id, modelComputeOptions, redactionHierarchyIds]);

    const handleModelOptionToggle = (
        checked: boolean,
        modelOptionName: string,
    ) => {
        setModelComputeOptions((prev) => {
            const option = prev?.find(
                (i) => i.modelComputeOption.modelOption === modelOptionName,
            );

            if (!prev || !option) return prev;
            if (checked) {
                option.enabled = true;
            } else {
                option.enabled = false;
            }
            return [...prev];
        });
    };

    const updateModelOptionIds = (ids: string[] | null, option: string) => {
        setModelComputeOptions((prev) => {
            const foundOption = prev?.find(
                (i) => i.modelComputeOption.modelOption === option,
            );
            if (!prev || !foundOption) return;
            foundOption.modelComputeOption.modelComputeStructureMembers =
                ids?.map((i) => ({ organizationStructureMemberId: i })) ?? [];
            return [...prev];
        });
    };

    const getSavedModelOption = (
        modelOptionName: string,
    ): ModelControlSnapshotModelOption | undefined => {
        if (!savedModelControlsSnapshot) {
            return undefined;
        }

        try {
            const snapshot = JSON.parse(
                savedModelControlsSnapshot,
            ) as ModelControlSnapshot;

            return snapshot.modelOptions?.find(
                (option) => option.modelOption === modelOptionName,
            );
        } catch {
            return undefined;
        }
    };

    const restoreModelOptionHierarchyFromDb = (modelOptionName: string) => {
        const savedOption = getSavedModelOption(modelOptionName);
        const hierarchyIds = savedOption?.organizationStructureMemberIds ?? [];

        updateModelOptionIds(hierarchyIds, modelOptionName);

        return {
            hierarchyIds,
            savedOption,
        };
    };

    const setSafetyEventsRunOnAll = (checked: boolean) => {
        setIsAll(checked);

        if (!checked) {
            const { savedOption } =
                restoreModelOptionHierarchyFromDb("SafetyEvents");

            handleUpdateSEModelFreq(
                savedOption?.quotaFrequency as
                    | ModelComputeFrequency
                    | undefined,
            );
            handleUpdateSEModelQuota(savedOption?.modelQuota);
        }
    };

    const setSafetyEventsEnabled = (checked: boolean) => {
        handleModelOptionToggle(checked, "SafetyEvents");

        if (checked) {
            const { hierarchyIds, savedOption } =
                restoreModelOptionHierarchyFromDb("SafetyEvents");

            handleUpdateSEModelFreq(
                savedOption?.quotaFrequency as
                    | ModelComputeFrequency
                    | undefined,
            );
            handleUpdateSEModelQuota(savedOption?.modelQuota);
            setIsAll(
                !(
                    savedOption?.quotaFrequency ||
                    savedOption?.modelQuota ||
                    hierarchyIds.length
                ),
            );
        }
    };

    const setIVREnabledFromControls = (checked: boolean) => {
        handleModelOptionToggle(checked, "IVR");

        if (checked) {
            const { hierarchyIds } = restoreModelOptionHierarchyFromDb("IVR");

            setSelectedIVRHierarchies(hierarchyIds);
            setIVRRunOnAll(hierarchyIds.length === 0);
        }
    };

    const setIVRRunOnAllFromControls = (checked: boolean) => {
        setIVRRunOnAll(checked);

        if (!checked) {
            const { hierarchyIds } = restoreModelOptionHierarchyFromDb("IVR");

            setSelectedIVRHierarchies(hierarchyIds);
        }
    };

    const setVoicemailEnabledFromControls = (checked: boolean) => {
        handleModelOptionToggle(checked, "Voicemail");

        if (checked) {
            const { hierarchyIds } =
                restoreModelOptionHierarchyFromDb("Voicemail");

            setSelectedVoicemailHierarchies(hierarchyIds);
            setVoicemailRunOnAll(hierarchyIds.length === 0);
        }
    };

    const setVoicemailRunOnAllFromControls = (checked: boolean) => {
        setVoicemailRunOnAll(checked);

        if (!checked) {
            const { hierarchyIds } =
                restoreModelOptionHierarchyFromDb("Voicemail");

            setSelectedVoicemailHierarchies(hierarchyIds);
        }
    };

    const getSavedLanguageDetectionValues = () => {
        if (!savedModelControlsSnapshot) {
            return {
                hierarchyIds: [] as string[],
                runOnAll: true,
            };
        }

        try {
            const snapshot = JSON.parse(
                savedModelControlsSnapshot,
            ) as ModelControlSnapshot;
            const hierarchyIds = snapshot.languageDetection?.hierarchyIds ?? [];
            const runOnAll =
                !!snapshot.languageDetection?.activeProcessingOptions?.length ||
                hierarchyIds.length === 0;

            return {
                hierarchyIds,
                runOnAll,
            };
        } catch {
            return {
                hierarchyIds: [] as string[],
                runOnAll: true,
            };
        }
    };

    const setLanguageDetectionEnabledFromControls = (checked: boolean) => {
        setLanguageDetectionIsEnabled(checked);

        if (checked) {
            const { hierarchyIds, runOnAll } =
                getSavedLanguageDetectionValues();

            setSelectedLanguageDetectionHierarchiesIds(hierarchyIds);
            setLanguageDetectionRunOnAll(runOnAll);
        }
    };

    const setLanguageDetectionRunOnAllFromControls = (checked: boolean) => {
        setLanguageDetectionRunOnAll(checked);

        if (!checked) {
            const { hierarchyIds } = getSavedLanguageDetectionValues();

            setSelectedLanguageDetectionHierarchiesIds(hierarchyIds);
        }
    };

    const setExtractPIIEnabledFromControls = (checked: boolean) => {
        setExtractPIIEnabled(checked);

        if (checked) {
            updateModelOptionIds(
                extractPIIDbStateRef.current.hierarchyIds,
                "Redaction",
            );
            setActivePIIOptions(extractPIIDbStateRef.current.activePIIOptions);
        }
    };

    const handleQosFastLaneHierarchyChange = (hierarchyIds: string[]) => {
        setQosFastLaneHierarchyIds(hierarchyIds);
        setQosBulkLaneHierarchyIds((previousValue) =>
            previousValue.filter((id) => !hierarchyIds.includes(id)),
        );
    };

    const handleQosBulkLaneHierarchyChange = (hierarchyIds: string[]) => {
        setQosBulkLaneHierarchyIds(hierarchyIds);
        setQosFastLaneHierarchyIds((previousValue) =>
            previousValue.filter((id) => !hierarchyIds.includes(id)),
        );
    };

    const saveQosConfigurations = async () => {
        if (!id) return;

        const managedQosServiceLevels = new Set<QosServiceLevel>([
            QosServiceLevel.Highest,
            QosServiceLevel.Bulk,
        ]);

        // Desired QoS state from the current UI selections.
        const desiredQosConfigurationByHierarchyId = new Map<
            string,
            QosServiceLevel
        >();

        // Existing QoS state from the API response (active + inactive).
        const existingQosConfigurationByHierarchyId = new Map<
            string,
            QosConfiguration
        >();

        if (qosEnabled) {
            qosFastLaneHierarchyIds.forEach((hierarchyId) => {
                desiredQosConfigurationByHierarchyId.set(
                    hierarchyId.toLowerCase(),
                    QosServiceLevel.Highest,
                );
            });

            qosBulkLaneHierarchyIds.forEach((hierarchyId) => {
                desiredQosConfigurationByHierarchyId.set(
                    hierarchyId.toLowerCase(),
                    QosServiceLevel.Bulk,
                );
            });
        }

        qosConfigurations.forEach((configuration) => {
            if (!managedQosServiceLevels.has(configuration.serviceLevel)) {
                return;
            }

            const hierarchyId =
                configuration.organizationStructureMemberId.toLowerCase();
            const existingConfiguration =
                existingQosConfigurationByHierarchyId.get(hierarchyId);

            // If duplicates already exist, prefer the active row.
            if (
                existingConfiguration?.isActive === false &&
                configuration.isActive !== false
            ) {
                existingQosConfigurationByHierarchyId.set(
                    hierarchyId,
                    configuration,
                );
                return;
            }

            if (!existingConfiguration) {
                existingQosConfigurationByHierarchyId.set(
                    hierarchyId,
                    configuration,
                );
            }
        });

        const qosConfigurationMutations: Promise<unknown>[] = [];

        const hierarchyIdsToProcess = new Set<string>([
            ...existingQosConfigurationByHierarchyId.keys(),
            ...desiredQosConfigurationByHierarchyId.keys(),
        ]);

        hierarchyIdsToProcess.forEach((hierarchyId) => {
            const desiredServiceLevel =
                desiredQosConfigurationByHierarchyId.get(hierarchyId);
            const existingConfiguration =
                existingQosConfigurationByHierarchyId.get(hierarchyId);

            // No desired level for this hierarchy: soft-delete only if active.
            if (desiredServiceLevel === undefined) {
                if (
                    existingConfiguration &&
                    existingConfiguration.isActive !== false
                ) {
                    qosConfigurationMutations.push(
                        qosService.deleteQosConfiguration(
                            existingConfiguration.id,
                        ),
                    );
                }
                return;
            }

            // Reuse existing row (including inactive rows) to avoid duplicate inserts.
            if (existingConfiguration) {
                const isActive = existingConfiguration.isActive !== false;
                if (
                    isActive &&
                    existingConfiguration.serviceLevel === desiredServiceLevel
                ) {
                    return;
                }
                qosConfigurationMutations.push(
                    qosService.updateQosConfiguration(
                        existingConfiguration.id,
                        {
                            ...existingConfiguration,
                            serviceLevel: desiredServiceLevel,
                            isActive: true,
                        },
                    ),
                );
                return;
            }

            //Add QOS config
            const qosConfigurationToCreate: QosConfiguration = {
                id: uuidv4(),
                organizationId: id,
                organizationStructureMemberId: hierarchyId,
                serviceLevel: desiredServiceLevel,
                isActive: true,
            };

            qosConfigurationMutations.push(
                qosService.createQosConfiguration(qosConfigurationToCreate),
            );
        });

        //Run all mutations and update configs
        await Promise.all(qosConfigurationMutations);
        const persistedQosConfigurations =
            await qosService.getQosConfigurations();
        setQosState(persistedQosConfigurations);
    };

    const handleUpdateSEModelQuota = (quota?: number) => {
        setModelComputeOptions((prev) => {
            const seOption = prev?.find(
                (i) => i.modelComputeOption.modelOption === "SafetyEvents",
            );
            if (!prev || !seOption) return;
            seOption.modelComputeOption.modelQuota = quota;
            return [...prev];
        });
    };
    const handleUpdateSEModelFreq = (freq?: ModelComputeFrequency) => {
        setModelComputeOptions((prev) => {
            const seOption = prev?.find(
                (i) => i.modelComputeOption.modelOption === "SafetyEvents",
            );
            if (!prev || !seOption) return;
            seOption.modelComputeOption.quotaFrequency = freq;
            return [...prev];
        });
    };

    const SEConvoNum = modelComputeOptions?.find(
        (i) => i.modelComputeOption.modelOption === "SafetyEvents",
    )?.modelComputeOption.modelQuota;

    const missingSomething = !SEDefaultFreq || !SEDefaultQuota;

    const audioTranscriptionFilterOption = transcriptionFilterOptions?.find(
        (tfo) =>
            tfo.transcriptionFilterOption.mediaType === InteractionType.Audio,
    );

    const audioWordCountValue =
        audioTranscriptionFilterOption?.transcriptionFilterOption
            ?.filterValue ?? 0;

    const sortedValues = (values?: string[] | null) =>
        [...(values ?? [])].sort();

    const shouldClearModelHierarchyForSave = React.useCallback(
        (modelOptionName: string) =>
            (modelOptionName === "Redaction" && !extractPIIEnabled) ||
            (["SafetyEvents", ...PV_MODEL_OPTIONS].includes(modelOptionName) &&
                (!SEEnabled || isAll)) ||
            (modelOptionName === "IVR" && (!ivrIsEnabled || ivrRunOnAll)) ||
            (modelOptionName === "Voicemail" &&
                (!voicemailIsEnabled || voicemailRunOnAll)),
        [
            extractPIIEnabled,
            isAll,
            ivrIsEnabled,
            ivrRunOnAll,
            SEEnabled,
            voicemailIsEnabled,
            voicemailRunOnAll,
        ],
    );

    const currentModelControlsSnapshot = React.useMemo(() => {
        const safetyEventModelOption = modelComputeOptions?.find(
            (i) => i.modelComputeOption.modelOption === "SafetyEvents",
        )?.modelComputeOption;

        const modelOptions = modelComputeOptions?.map((option) => {
            const modelComputeOption = option.modelComputeOption;
            const shouldUseSafetyEventValues = PV_MODEL_OPTIONS.includes(
                modelComputeOption.modelOption,
            );
            const sourceModelOption =
                shouldUseSafetyEventValues && safetyEventModelOption
                    ? safetyEventModelOption
                    : modelComputeOption;

            const shouldClearHierarchy = shouldClearModelHierarchyForSave(
                modelComputeOption.modelOption,
            );
            const shouldClearSafetyValues =
                ["SafetyEvents", ...PV_MODEL_OPTIONS].includes(
                    modelComputeOption.modelOption,
                ) &&
                (!SEEnabled || isAll);
            const organizationStructureMemberIds = shouldClearHierarchy
                ? []
                : sourceModelOption.modelComputeStructureMembers?.map(
                      (i) => i.organizationStructureMemberId,
                  ) ?? [];
            const runOnAllHierarchies =
                modelComputeOption.modelOption === "SafetyEvents"
                    ? isAll
                    : modelComputeOption.modelOption === "IVR" ||
                      modelComputeOption.modelOption === "Voicemail"
                    ? modelComputeOption.modelOption === "IVR"
                        ? ivrRunOnAll
                        : voicemailRunOnAll
                    : undefined;

            return {
                modelOption: modelComputeOption.modelOption,
                enabled: shouldUseSafetyEventValues
                    ? SEEnabled
                    : option.enabled,
                runOnAllHierarchies,
                organizationStructureMemberIds: sortedValues(
                    organizationStructureMemberIds,
                ),
                quotaFrequency: shouldClearSafetyValues
                    ? undefined
                    : sourceModelOption.quotaFrequency,
                modelQuota: shouldClearSafetyValues
                    ? undefined
                    : sourceModelOption.modelQuota,
            };
        });

        const redactionProcessingOptions = [
            ...(activeRedactionOptions ?? []),
            ...(extractPIIEnabled ? [extractPIIString] : []),
            ...(persistOriginalTranscript ? [persistTranscriptString] : []),
        ];

        return JSON.stringify({
            modelOptions,
            transcriptionFilterOptions: transcriptionFilterOptions?.map(
                (option) => ({
                    filterType: option.transcriptionFilterOption.filterType,
                    enabled: option.enabled,
                    mediaType: option.transcriptionFilterOption.mediaType,
                    filterValue: option.transcriptionFilterOption.filterValue,
                }),
            ),
            redactionProcessingOptions: sortedValues(
                redactionProcessingOptions,
            ),
            piiExtractionOptions:
                extractPIIEnabled && activePIIOptions
                    ? sortedValues(activePIIOptions)
                    : [],
            voiceRedactionFactor,
            hotWords: {
                transcriptionWords: hotWordsIsEnabled
                    ? sortedValues(txHotWords)
                    : [],
                redactionWords: redactHotWordsIsEnabled
                    ? sortedValues(redactHotWords)
                    : [],
            },
            languageDetection: {
                activeProcessingOptions:
                    languageDetectionRunOnAll && languageDetectionIsEnabled
                        ? [languageDetectionProcessingId]
                        : [],
                runOnAllHierarchies:
                    languageDetectionIsEnabled && languageDetectionRunOnAll,
                hierarchyIds:
                    !languageDetectionRunOnAll && languageDetectionIsEnabled
                        ? sortedValues(selectedLanguageDetectionHierarchiesIds)
                        : [],
            },
            qos: {
                enabled: qosEnabled,
                fastLaneHierarchyIds: qosEnabled
                    ? sortedValues(qosFastLaneHierarchyIds)
                    : [],
                bulkLaneHierarchyIds: qosEnabled
                    ? sortedValues(qosBulkLaneHierarchyIds)
                    : [],
            },
        });
    }, [
        activePIIOptions,
        activeRedactionOptions,
        extractPIIEnabled,
        hotWordsIsEnabled,
        ivrRunOnAll,
        languageDetectionIsEnabled,
        languageDetectionProcessingId,
        languageDetectionRunOnAll,
        modelComputeOptions,
        persistOriginalTranscript,
        qosBulkLaneHierarchyIds,
        qosEnabled,
        qosFastLaneHierarchyIds,
        redactHotWords,
        redactHotWordsIsEnabled,
        selectedLanguageDetectionHierarchiesIds,
        shouldClearModelHierarchyForSave,
        transcriptionFilterOptions,
        txHotWords,
        voicemailRunOnAll,
        voiceRedactionFactor,
        SEEnabled,
        isAll,
    ]);

    React.useEffect(() => {
        if (
            savedModelControlsSnapshot ||
            modelOptionsIsLoading ||
            hotWordsIsLoading ||
            languageDetectionIsLoading ||
            redactionOptionsIsLoading ||
            qosIsLoading ||
            !modelComputeOptions ||
            !transcriptionFilterOptions ||
            activeRedactionOptions === undefined ||
            activePIIOptions === undefined
        ) {
            return;
        }

        setSavedModelControlsSnapshot(currentModelControlsSnapshot);
    }, [
        activePIIOptions,
        activeRedactionOptions,
        currentModelControlsSnapshot,
        hotWordsIsLoading,
        languageDetectionIsLoading,
        modelComputeOptions,
        modelOptionsIsLoading,
        qosIsLoading,
        redactionOptionsIsLoading,
        savedModelControlsSnapshot,
        transcriptionFilterOptions,
    ]);

    const safetyEventMissingHierarchy =
        SEEnabled && !isAll && SEDefaultOSMIds.length === 0;
    const safetyEventMissingVolumeControls =
        safetyEventMissingHierarchy && missingSomething;
    const safetyEventInvalidConversationCount =
        SEEnabled && !isAll && (SEConvoNum ?? 1) <= 0;
    const extractPIIMissingHierarchy =
        extractPIIEnabled && redactionHierarchyIds.length === 0;
    const ivrMissingHierarchy =
        ivrIsEnabled && !ivrRunOnAll && selectedIVRHierarchies.length === 0;
    const voicemailMissingHierarchy =
        voicemailIsEnabled &&
        !voicemailRunOnAll &&
        selectedVoicemailHierarchies.length === 0;
    const languageDetectionMissingHierarchy =
        languageDetectionIsEnabled &&
        !languageDetectionRunOnAll &&
        selectedLanguageDetectionHierarchiesIds.length === 0;
    const modelRunningOnZeroHierarchies =
        safetyEventMissingHierarchy ||
        extractPIIMissingHierarchy ||
        ivrMissingHierarchy ||
        voicemailMissingHierarchy ||
        languageDetectionMissingHierarchy;
    const modelControlsInvalid =
        modelRunningOnZeroHierarchies ||
        safetyEventMissingVolumeControls ||
        safetyEventInvalidConversationCount ||
        audioWordCountValue < 0;
    const modelControlsChanged =
        !savedModelControlsSnapshot ||
        savedModelControlsSnapshot !== currentModelControlsSnapshot;

    // save should be disabled if enabled hierarchy-scoped models have no hierarchies selected,
    // SE volume controls are invalid, or audioWordCountValue is negative
    const saveButtonDisabled = modelControlsInvalid || !modelControlsChanged;

    const saveButtonTooltip = modelRunningOnZeroHierarchies
        ? "A model is running on 0 hierarchies, please check all model settings"
        : !modelControlsChanged
        ? "Nothing is changed"
        : modelControlsInvalid
        ? "You must specify valid model controls. Check fields for errors."
        : "";
    const modelControlChangeSummary = getModelControlChangeSummary(
        savedModelControlsSnapshot,
        currentModelControlsSnapshot,
        hierarchyLabelById,
    );

    const saveModelControls = async () => {
        if (
            !id ||
            !modelComputeOptions ||
            !transcriptionFilterOptions ||
            isViewOnly
        )
            return;

        try {
            setSavingModelCompute(true);
            setSaveConfirmationOpen(false);

            // Grab safety event model option
            const safetyEventModelOption = modelComputeOptions.find(
                (i) => i.modelComputeOption.modelOption === "SafetyEvents",
            );

            const getModelOptionSaveValues = (
                option: NonNullable<typeof modelComputeOptions>[number],
            ) => {
                const modelComputeOption = option.modelComputeOption;
                const shouldClearHierarchy = shouldClearModelHierarchyForSave(
                    modelComputeOption.modelOption,
                );
                const shouldClearSafetyValues =
                    ["SafetyEvents", ...PV_MODEL_OPTIONS].includes(
                        modelComputeOption.modelOption,
                    ) &&
                    (!SEEnabled || isAll);

                const shouldUseSafetyEventValues = PV_MODEL_OPTIONS.includes(
                    modelComputeOption.modelOption,
                );
                const sourceModelOption =
                    shouldUseSafetyEventValues && safetyEventModelOption
                        ? safetyEventModelOption.modelComputeOption
                        : modelComputeOption;

                return {
                    organizationStructureMemberIds: shouldClearHierarchy
                        ? []
                        : sourceModelOption.modelComputeStructureMembers?.map(
                              (i) => i.organizationStructureMemberId,
                          ) ?? [],
                    quotaFrequency: shouldClearSafetyValues
                        ? undefined
                        : sourceModelOption.quotaFrequency,
                    modelQuota: shouldClearSafetyValues
                        ? undefined
                        : sourceModelOption.modelQuota,
                    // For PV model options, clone the enabled value from SafetyEvents
                    // This won't impact how classifiers run non PV tenants because we only seed those ModelComputeOptions for AE/AEp2/PQC when we are ready to set up a tenant for PV
                    enabled:
                        shouldUseSafetyEventValues && safetyEventModelOption
                            ? safetyEventModelOption.enabled
                            : option.enabled,
                };
            };

            await orgService.updateModelComputeOptions(
                id,
                modelComputeOptions.map((i) => {
                    const saveValues = getModelOptionSaveValues(i);

                    return {
                        modelOption: i.modelComputeOption.modelOption,
                        enabled: saveValues.enabled,
                        organizationStructureMemberIds:
                            saveValues.organizationStructureMemberIds,
                        quotaFrequency: saveValues.quotaFrequency,
                        modelQuota: saveValues.modelQuota,
                    };
                }),
            );
            await orgService.updateTranscriptionFilterOptions(
                id,
                transcriptionFilterOptions.map((i) => ({
                    filterType: i.transcriptionFilterOption.filterType,
                    enabled: i.enabled,
                    mediaType: i.transcriptionFilterOption.mediaType,
                    filterValue: i.transcriptionFilterOption.filterValue,
                })),
            );

            const formattedActiveRedactionOptions = [
                ...(activeRedactionOptions ?? []),
            ];

            if (extractPIIEnabled) {
                formattedActiveRedactionOptions.push(extractPIIString);
            }

            if (persistOriginalTranscript) {
                formattedActiveRedactionOptions.push(persistTranscriptString);
            }

            await orgService.updateRedactionProcessingOptions(
                id,
                formattedActiveRedactionOptions ?? [],
                redactionOptions ?? [],
            );

            await orgService.updatePIIExtractionOptions(
                id,
                // if Extract PII is disabled, dont save any PII Options
                extractPIIEnabled && activePIIOptions ? activePIIOptions : [],
                PIIOptions ?? [],
            );

            const savedExtractPIIHierarchyIds = extractPIIEnabled
                ? redactionHierarchyIds
                : [];
            const savedActivePIIOptions =
                extractPIIEnabled && activePIIOptions ? activePIIOptions : [];

            if (!hotWordsIsEnabled) {
                setTxHotWords([]);
            }

            await modelComputeService.setHotwords({
                organizationId: id,
                transcriptionWords: hotWordsIsEnabled ? txHotWords : [],
                redactionWords: redactHotWordsIsEnabled ? redactHotWords : [],
            });

            if (!languageDetectionIsEnabled || languageDetectionRunOnAll) {
                setSelectedLanguageDetectionHierarchiesIds([]);
            }

            await modelComputeService.setActiveModelControlProcessingOptions(
                !languageDetectionRunOnAll || !languageDetectionIsEnabled
                    ? []
                    : [languageDetectionProcessingId],
            );

            await modelComputeService.setLanguageDetectionHierarchies(
                languageDetectionRunOnAll || !languageDetectionIsEnabled
                    ? []
                    : selectedLanguageDetectionHierarchiesIds,
            );

            await saveQosConfigurations();

            if (formattedActiveRedactionOptions.includes("VoiceRedaction")) {
                await orgService.updateVoiceRedactionFactor(
                    id,
                    voiceRedactionFactor,
                );
            }

            if (!extractPIIEnabled) {
                updateModelOptionIds([], "Redaction");
                setActivePIIOptions([]);
            }

            if (!SEEnabled || isAll) {
                updateModelOptionIds([], "SafetyEvents");
                handleUpdateSEModelFreq(undefined);
                handleUpdateSEModelQuota(undefined);
            }

            if (!ivrIsEnabled || ivrRunOnAll) {
                updateModelOptionIds([], "IVR");
                setSelectedIVRHierarchies([]);
            }

            if (!voicemailIsEnabled || voicemailRunOnAll) {
                updateModelOptionIds([], "Voicemail");
                setSelectedVoicemailHierarchies([]);
            }

            extractPIIDbStateRef.current = {
                initializedForOrgId: id,
                hierarchyIds: savedExtractPIIHierarchyIds,
                activePIIOptions: savedActivePIIOptions,
            };
            setSavedModelControlsSnapshot(currentModelControlsSnapshot);

            messageStore.logMessage("Model compute options saved", "success");
        } catch (e) {
            messageStore.logMessage(
                "Failed to save model compute options",
                "error",
            );
        } finally {
            setSavingModelCompute(false);
        }
    };

    const modelControlPage = (
        <Grid
            container
            sx={{ width: "95%", p: 2 }}
            alignContent={"flex-start"}
            gap={3}
        >
            <Grid
                container
                item
                xs={12}
                sx={{ fontWeight: 600, color: theme.palette.gray[400] }}
            >
                Admin {">"} Organization Setup {">"}
                <span
                    style={{
                        fontWeight: "bold",
                        paddingLeft: 4,
                        color: theme.palette.black.main,
                    }}
                >
                    Model Control
                </span>
            </Grid>
            <Grid
                container
                item
                xs={12}
                justifyContent="space-between"
                alignItems="flex-start"
            >
                {/* Left: title stacked over the security note */}
                <Grid container item xs direction="column">
                    <Grid item>
                        <span style={{ fontWeight: 600, fontSize: "24px" }}>
                            Model Control
                        </span>
                    </Grid>
                    {isViewOnly && (
                        <Grid item>
                            <span
                                style={{
                                    fontWeight: 600,
                                    color: theme.palette.gray[400],
                                    fontSize: "12px",
                                    marginTop: 4, // slight space under title
                                }}
                            >
                                For security reasons, please submit a Helpdesk
                                ticket to helpdesk@authenticx.com to have any
                                settings on this page changed
                            </span>
                        </Grid>
                    )}
                </Grid>

                {/* Right: button remains on the right */}
                <Grid item>
                    <AcxButton
                        fullWidth
                        onClick={() => setSaveConfirmationOpen(true)}
                        disabled={saveButtonDisabled || isViewOnly}
                        loading={savingModelCompute}
                        tooltip={saveButtonDisabled ? saveButtonTooltip : ""}
                    >
                        Save model control settings
                    </AcxButton>
                </Grid>
            </Grid>
            <AYSDialog.Container
                isOpen={saveConfirmationOpen}
                onClose={() => setSaveConfirmationOpen(false)}
                maxWidth="sm"
                id="confirm-model-control-changes"
                icon={
                    <Box>
                        <Box
                            sx={{
                                alignItems: "center",
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: "50%",
                                color: theme.palette.white.main,
                                display: "flex",
                                fontSize: "20px",
                                fontWeight: 700,
                                height: 32,
                                justifyContent: "center",
                                lineHeight: "32px",
                                marginBottom: theme.spacing(2),
                                width: 32,
                            }}
                        >
                            !
                        </Box>
                    </Box>
                }
            >
                <AYSDialog.Title>
                    Confirm changes to Model Controls
                </AYSDialog.Title>
                <AYSDialog.Subtitle>
                    Review the changes below before saving. These updates will
                    take effect immediately and cannot be undone.
                </AYSDialog.Subtitle>
                <AYSDialog.Content
                    sx={{
                        color: theme.palette.gray[700],
                        fontSize: "14px",
                        lineHeight: "20px",
                        padding: `${theme.spacing(2, 3)} !important`,
                    }}
                >
                    <Box
                        component="ul"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            margin: 0,
                            paddingLeft: 2,
                        }}
                    >
                        {modelControlChangeSummary.map((change) => (
                            <Box component="li" key={getChangeKey(change)}>
                                <Typography
                                    component="div"
                                    sx={{
                                        color: theme.palette.gray[700],
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        lineHeight: "20px",
                                    }}
                                >
                                    {change.property}
                                </Typography>
                                {change.addedValues !== undefined ||
                                change.removedValues !== undefined ? (
                                    <>
                                        <Typography
                                            component="div"
                                            sx={{
                                                color: theme.palette.gray[600],
                                                fontSize: "13px",
                                                lineHeight: "18px",
                                            }}
                                        >
                                            Added: {change.addedValues}
                                        </Typography>
                                        <Typography
                                            component="div"
                                            sx={{
                                                color: theme.palette.gray[600],
                                                fontSize: "13px",
                                                lineHeight: "18px",
                                            }}
                                        >
                                            Removed: {change.removedValues}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography
                                            component="div"
                                            sx={{
                                                color: theme.palette.gray[600],
                                                fontSize: "13px",
                                                lineHeight: "18px",
                                            }}
                                        >
                                            Previous: {change.previousValue}
                                        </Typography>
                                        <Typography
                                            component="div"
                                            sx={{
                                                color: theme.palette.gray[600],
                                                fontSize: "13px",
                                                lineHeight: "18px",
                                            }}
                                        >
                                            New: {change.newValue}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        ))}
                    </Box>
                </AYSDialog.Content>
                <AYSDialog.Actions>
                    <AcxButton
                        disabled={savingModelCompute}
                        onClick={() => setSaveConfirmationOpen(false)}
                        color="white"
                        id="cancel-confirm-model-control-changes"
                    >
                        Cancel
                    </AcxButton>
                    <AcxButton
                        loading={savingModelCompute}
                        onClick={saveModelControls}
                        disabled={savingModelCompute}
                    >
                        Save Changes
                    </AcxButton>
                </AYSDialog.Actions>
            </AYSDialog.Container>
            <Grid container item xs={12}>
                <Paper sx={{ width: "100%", paddingBottom: theme.spacing(2) }}>
                    <Grid container item xs={12} direction="column">
                        <Grid
                            item
                            xs={12}
                            sx={{
                                p: 2,
                                pb: 0,
                                fontWeight: 600,
                                fontSize: "16px",
                            }}
                        >
                            Model Compute Management
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{ p: 2, pt: 0, color: theme.palette.gray[500] }}
                        >
                            Choose which models to run
                        </Grid>
                        <Divider />
                        <Grid container>
                            <Grid
                                container
                                item
                                xs={12}
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.gray[400],
                                    fontSize: "12px",
                                }}
                            >
                                <Grid item xs={2} sx={{ p: 2 }}>
                                    Model
                                </Grid>
                                <Grid item xs={7} sx={{ p: 2 }}>
                                    Description
                                </Grid>
                                <Grid item xs={2} sx={{ p: 2 }}>
                                    Enable
                                </Grid>
                            </Grid>
                            <SafetyEventModelControls
                                isViewOnly={isViewOnly}
                                SEEnabled={SEEnabled}
                                setIsAll={setSafetyEventsRunOnAll}
                                isAll={isAll}
                                orgModelComputeOptions={modelComputeOptions}
                                orgId={id ?? ""}
                                handleUpdateSEModelOSMIds={(ids) =>
                                    updateModelOptionIds(ids, "SafetyEvents")
                                }
                                handleModelOptionToggle={(checked) =>
                                    setSafetyEventsEnabled(checked)
                                }
                                handleUpdateSEModelFreq={
                                    handleUpdateSEModelFreq
                                }
                                handleUpdateSEModelQuota={
                                    handleUpdateSEModelQuota
                                }
                                SEConvoNum={SEConvoNum}
                                SEDefaultFreq={SEDefaultFreq}
                            />
                            <RedactionModelControls
                                isViewOnly={isViewOnly}
                                redactionEnabled={!!redactionOption?.enabled}
                                orgRedactionOptions={
                                    multiSelectRedactionOptions
                                }
                                localActiveOrgRedactionOptions={
                                    activeRedactionOptions
                                }
                                setLocalActiveOrgRedactionOptions={
                                    setActiveRedactionOptions
                                }
                                hierarchiesToExtractPII={redactionHierarchyIds}
                                setHierarchiesToExtractPII={(ids) =>
                                    updateModelOptionIds(ids, "Redaction")
                                }
                                localPersistOriginalTranscript={
                                    persistOriginalTranscript
                                }
                                setLocalPersistOriginalTranscript={
                                    setPersistOriginalTranscript
                                }
                                localExtractPIIEnabled={extractPIIEnabled}
                                setLocalExtractPIIEnabled={
                                    setExtractPIIEnabledFromControls
                                }
                                PIIOptions={PIIOptions}
                                localActivePIIOptions={activePIIOptions}
                                setLocalActivePIIOptions={setActivePIIOptions}
                                orgId={id ?? ""}
                                handleModelOptionToggle={
                                    handleModelOptionToggle
                                }
                                voiceRedactionFactor={voiceRedactionFactor}
                                setVoiceRedactionFactor={
                                    setVoiceRedactionFactor
                                }
                                redactHotWords={redactHotWords}
                                setRedactHotWords={setRedactHotWords}
                                redactHotWordsIsEnabled={
                                    redactHotWordsIsEnabled
                                }
                                setRedactHotWordsIsEnabled={
                                    setRedactHotWordsIsEnabled
                                }
                            />
                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Eddy Effect
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Surfaces when a desired or expected customer
                                    experience is disrupted by an obstacle,
                                    along with identifying where in the
                                    interaction the friction point was
                                    experienced.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "EddyEffect",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "EddyEffect",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}>
                                <Grid
                                    sx={{
                                        padding: theme.spacing(2),
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        flexDirection: "row",
                                        display: "flex",
                                        alignItems: "flex-start",
                                    }}
                                    xs={2}
                                    item
                                >
                                    Patient Journey Stage
                                    <Grid
                                        sx={{
                                            position: "relative",
                                            top: "-14%",
                                        }}
                                    >
                                        <PatientJourneyStagesDialog />
                                    </Grid>
                                </Grid>

                                <Grid
                                    item
                                    xs={7}
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: "14px",
                                        color: theme.palette.gray.main,
                                        p: 2,
                                    }}
                                >
                                    Automatically predicts the patient journey
                                    stage for every conversation, based on
                                    standard treatment journey stages for
                                    Pharma.
                                </Grid>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "PatientJourney",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "PatientJourney",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Sentiment
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Labels the starting and ending sentiments of
                                    a conversation as Positive, Neutral, or
                                    Negative.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "Sentiment",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "Sentiment",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Summary
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Summarizes the main points of a
                                    conversation.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "Summary",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "Summary",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Drivers
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Surfaces up to 3 Drivers of conversation for
                                    every interaction, by grouping individual,
                                    closely-related words into Drivers.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    ModelOption.Driver,
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                ModelOption.Driver,
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Contact Type
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Automatically identifies the primary persona
                                    present on every interaction as one of the
                                    following 6 contact types: Caregiver,
                                    Patient/Member, HCP, Payer, Internal,
                                    Pharmacy.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "ContactType",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "ContactType",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    HIPAA
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Flags whether the agent confirms name and 2
                                    pieces of PII before disclosing HIPAA
                                    information.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "Hipaa",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "Hipaa",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <VoicemailControls
                                selectedHierarchiesIds={
                                    selectedVoicemailHierarchies
                                }
                                setSelectedHierarchiesIds={(ids) => {
                                    updateModelOptionIds(ids, "Voicemail");
                                    setSelectedVoicemailHierarchies(ids);
                                }}
                                isEnabled={voicemailIsEnabled}
                                setIsEnabled={setVoicemailEnabledFromControls}
                                setRunOnAll={setVoicemailRunOnAllFromControls}
                                runOnAll={voicemailRunOnAll}
                                orgId={id}
                                isViewOnly={isViewOnly}
                            />

                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Word Count
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    <Grid xs={12}>
                                        Detects audio interactions with fewer
                                        than the designated word count, and
                                        filters these conversations out.
                                    </Grid>
                                    {audioTranscriptionFilterOption?.enabled && (
                                        <Grid
                                            xs={12}
                                            padding={theme.spacing(1)}
                                            container
                                            alignItems={"center"}
                                        >
                                            Filter out audio conversations with
                                            a word count below:
                                            <AcxMainTextField
                                                id="test"
                                                type="number"
                                                value={audioWordCountValue}
                                                onChange={(e) => {
                                                    setTranscriptionFilterOptions(
                                                        (prev) => {
                                                            const audioOpton =
                                                                prev?.find(
                                                                    (i) =>
                                                                        i
                                                                            .transcriptionFilterOption
                                                                            .mediaType ===
                                                                        InteractionType.Audio,
                                                                );
                                                            if (
                                                                !prev ||
                                                                !audioOpton
                                                            )
                                                                return;
                                                            audioOpton.transcriptionFilterOption.filterValue =
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                );
                                                            return [...prev];
                                                        },
                                                    );
                                                }}
                                                isDisabled={isViewOnly}
                                                error={audioWordCountValue < 0}
                                                errorHelper="Value must be greater than 0"
                                            />
                                        </Grid>
                                    )}
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!transcriptionFilterOptions?.find(
                                                (i) =>
                                                    i.transcriptionFilterOption
                                                        .mediaType ===
                                                    InteractionType.Audio,
                                            )?.enabled
                                        }
                                        onChange={(e, checked) => {
                                            setTranscriptionFilterOptions(
                                                (prev) => {
                                                    const audioOption =
                                                        prev?.find(
                                                            (i) =>
                                                                i
                                                                    .transcriptionFilterOption
                                                                    .mediaType ===
                                                                InteractionType.Audio,
                                                        );
                                                    if (!prev || !audioOption)
                                                        return;
                                                    if (checked) {
                                                        audioOption.enabled =
                                                            true;
                                                    } else {
                                                        audioOption.enabled =
                                                            false;
                                                    }
                                                    return [...prev];
                                                },
                                            );
                                        }}
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Speaker Identification
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    Labels the speakers in each conversation as
                                    Agent or Customer.
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={
                                            !!modelComputeOptions?.find(
                                                (i) =>
                                                    i.modelComputeOption
                                                        .modelOption ===
                                                    "SpeakerIdentification",
                                            )?.enabled
                                        }
                                        onChange={(e, checked) =>
                                            handleModelOptionToggle(
                                                checked,
                                                "SpeakerIdentification",
                                            )
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>

                            <IVRControls
                                selectedHierarchiesIds={selectedIVRHierarchies}
                                setSelectedHierarchiesIds={(ids) => {
                                    setSelectedIVRHierarchies(ids);
                                    updateModelOptionIds(ids, "IVR");
                                }}
                                isEnabled={ivrIsEnabled}
                                setIsEnabled={setIVREnabledFromControls}
                                setRunOnAll={setIVRRunOnAllFromControls}
                                runOnAll={ivrRunOnAll}
                                orgId={id}
                                isViewOnly={isViewOnly}
                            />

                            <HotWordsControls
                                hotWords={txHotWords}
                                setHotWords={setTxHotWords}
                                hotWordsIsEnabled={hotWordsIsEnabled}
                                setHotWordsIsEnabled={setHotWordsIsEnabled}
                                isViewOnly={isViewOnly}
                            />
                            <LanguageDetectionControls
                                selectedHierarchiesIds={
                                    selectedLanguageDetectionHierarchiesIds
                                }
                                setSelectedHierarchiesIds={
                                    setSelectedLanguageDetectionHierarchiesIds
                                }
                                isEnabled={languageDetectionIsEnabled}
                                setIsEnabled={
                                    setLanguageDetectionEnabledFromControls
                                }
                                setRunOnAll={
                                    setLanguageDetectionRunOnAllFromControls
                                }
                                runOnAll={languageDetectionRunOnAll}
                                orgId={id}
                                isViewOnly={isViewOnly}
                            />
                            <Grid container item xs={12}>
                                <ModelHeaderText item xs={2}>
                                    Quality of Service
                                </ModelHeaderText>
                                <ModelDescText item xs={7}>
                                    <Grid container item xs={12} rowGap={2}>
                                        <Grid item xs={12}>
                                            Determines which hierarchies should
                                            be in non-standard pipeline
                                            processing lanes. Hierarchies not
                                            assigned to Fast Lane or Bulk will
                                            remain in the Standard processing
                                            lane.
                                        </Grid>
                                        {qosEnabled && (
                                            <>
                                                <Grid item xs={12}>
                                                    <ServiceHierarchySelect
                                                        maxContainerHeight="200px"
                                                        orgId={id ?? ""}
                                                        value={
                                                            qosFastLaneHierarchyIds
                                                        }
                                                        onChange={
                                                            handleQosFastLaneHierarchyChange
                                                        }
                                                        fullWidth
                                                        label="Process the following hierarchies in the Fast Lane:"
                                                        placeholder="Select Hierarchies"
                                                        isDisabled={isViewOnly}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <ServiceHierarchySelect
                                                        maxContainerHeight="200px"
                                                        orgId={id ?? ""}
                                                        value={
                                                            qosBulkLaneHierarchyIds
                                                        }
                                                        onChange={
                                                            handleQosBulkLaneHierarchyChange
                                                        }
                                                        fullWidth
                                                        label="Process the following hierarchies in the Bulk lane:"
                                                        placeholder="Select Hierarchies"
                                                        isDisabled={isViewOnly}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </ModelDescText>
                                <Grid item xs={2}>
                                    <AcxSwitch
                                        checked={qosEnabled}
                                        onChange={(_, checked) =>
                                            setQosEnabled(checked)
                                        }
                                        disabled={isViewOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );

    if (
        !authStore.canUserEdit("Model Controls") &&
        !authStore.canUserView("Model Controls")
    ) {
        return <>No Access</>;
    }
    return modelOptionsIsLoading ||
        hotWordsIsLoading ||
        languageDetectionIsLoading ||
        !orgStore.hasLoadedRedaction ||
        qosIsLoading ? (
        <AcxLoadingIndicator size={75} alternate="PuffLoader" />
    ) : (
        modelControlPage
    );
});

export default OrganizationModelControls;
